"use client"

import { motion } from "framer-motion"
import { GraduationCap, ShoppingCart, Smartphone, Code2, ArrowLeft, Stethoscope, UtensilsCrossed, IdCard, QrCode, BellRing, MonitorSmartphone, BarChart3 } from "lucide-react"
import { Card, CardContent } from "@/components/ui/card"
import Link from "next/link"

const technologies = [
  { name: "React" },
  { name: "Node.js" },
  { name: "Laravel" },
  { name: "Flutter" },
  { name: "Next.js" },
  { name: "MongoDB" },
  { name: "MySQL" },
  { name: "AWS" },
]

export function FeaturesSection() {
  return (
    <section id="services" className="px-6 py-24">
      <div className="max-w-5xl mx-auto">
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5 }}
          className="text-center mb-12"
        >
          <p className="text-sm font-medium text-zinc-500 uppercase tracking-wider mb-4">خدماتنا</p>
          <h2 className="font-display text-3xl md:text-4xl font-bold text-zinc-100 mb-4">
            حلول برمجية متكاملة لنجاح أعمالك
          </h2>
          <p className="text-zinc-500 max-w-xl mx-auto text-balance">
            نقدم مجموعة شاملة من الخدمات البرمجية المصممة خصيصاً لتلبية احتياجات عملك
          </p>
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-5 gap-3">
          {/* Card 1 - منصات تعليمية (wider - 3 cols) */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay: 0.1 }}
            className="md:col-span-3"
          >
            <Card className="group h-full overflow-hidden border-zinc-800/50 bg-zinc-900/50 hover:border-zinc-700/50 transition-all duration-300 rounded-2xl">
              <CardContent className="p-6">
                <div className="flex items-center gap-3 mb-3">
                  <motion.div
                    className="w-10 h-10 rounded-xl bg-blue-900/30 flex items-center justify-center"
                    whileHover={{ rotate: [0, -10, 10, 0] }}
                    transition={{ duration: 0.5 }}
                  >
                    <GraduationCap className="w-5 h-5 text-blue-400 group-hover:text-blue-300 transition-colors" />
                  </motion.div>
                  <p className="font-heading font-semibold text-zinc-100">منصات تعليمية</p>
                </div>
                <p className="text-zinc-500 text-sm mb-5">
                  نطور منصات تعليمية متكاملة مع نظام إدارة المحتوى، الاختبارات، الشهادات، والدفع الإلكتروني
                </p>
                <div className="rounded-xl border border-zinc-800 bg-zinc-950 p-4 overflow-hidden">
                  <div className="flex items-center justify-between mb-4">
                    <div className="flex gap-1.5">
                      <div className="w-2.5 h-2.5 rounded-full bg-red-500" />
                      <div className="w-2.5 h-2.5 rounded-full bg-yellow-500" />
                      <div className="w-2.5 h-2.5 rounded-full bg-green-500" />
                    </div>
                    <span className="text-xs text-zinc-500">لوحة تحكم المنصة</span>
                  </div>
                  {/* Dashboard Preview */}
                  <div className="grid grid-cols-3 gap-3 mb-4">
                    {[
                      { label: "الطلاب", value: "1,250", change: "+15%" },
                      { label: "الدورات", value: "48", change: "+5" },
                      { label: "الإيرادات", value: "125K", change: "+22%" },
                    ].map((metric, i) => (
                      <motion.div
                        key={metric.label}
                        className="bg-zinc-900/50 rounded-lg p-2.5"
                        initial={{ opacity: 0, y: 10 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        transition={{ delay: 0.3 + i * 0.1 }}
                      >
                        <p className="text-zinc-500 text-xs mb-1">{metric.label}</p>
                        <div className="flex items-baseline gap-1.5">
                          <span className="text-zinc-100 font-semibold text-sm">{metric.value}</span>
                          <motion.span
                            className="text-green-400 text-xs"
                            animate={{ opacity: [0.5, 1, 0.5] }}
                            transition={{ duration: 2, repeat: Number.POSITIVE_INFINITY }}
                          >
                            {metric.change}
                          </motion.span>
                        </div>
                      </motion.div>
                    ))}
                  </div>
                  {/* Progress bars */}
                  <div className="space-y-2">
                    {["إكمال الدورات", "رضا الطلاب", "معدل التفاعل"].map((label, i) => (
                      <div key={label} className="flex items-center gap-2">
                        <span className="text-xs text-zinc-500 w-24">{label}</span>
                        <div className="flex-1 h-1.5 bg-zinc-800 rounded-full overflow-hidden">
                          <motion.div
                            className="h-full bg-gradient-to-l from-blue-500 to-blue-400 rounded-full"
                            initial={{ width: "0%" }}
                            whileInView={{ width: `${85 - i * 10}%` }}
                            viewport={{ once: true }}
                            transition={{ duration: 1, delay: 0.5 + i * 0.1 }}
                          />
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
                <Link
                  href="/lms"
                  className="mt-4 flex items-center justify-center gap-2 py-3 px-4 rounded-xl bg-blue-600 hover:bg-blue-500 text-white font-medium transition-colors w-full"
                >
                  <span>تفاصيل المنصة التعليمية</span>
                  <ArrowLeft className="w-4 h-4" />
                </Link>
              </CardContent>
            </Card>
          </motion.div>

          {/* Card - سيستم السناتر (2 cols, next to the LMS card) */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay: 0.12 }}
            className="md:col-span-2"
          >
            <Card className="group h-full overflow-hidden border-zinc-800/50 bg-gradient-to-br from-zinc-900/50 to-sky-900/20 hover:border-sky-700/50 transition-all duration-300 rounded-2xl">
              <CardContent className="p-6 flex flex-col h-full">
                <div className="flex items-center gap-3 mb-3">
                  <motion.div
                    className="w-10 h-10 rounded-xl bg-sky-900/30 flex items-center justify-center"
                    whileHover={{ rotate: [0, -10, 10, 0] }}
                    transition={{ duration: 0.5 }}
                  >
                    <IdCard className="w-5 h-5 text-sky-400 group-hover:text-sky-300 transition-colors" />
                  </motion.div>
                  <p className="font-heading font-semibold text-zinc-100">سيستم السناتر</p>
                </div>
                <p className="text-zinc-500 text-sm mb-5">
                  حضور وانصراف بكروت NFC، وواتساب فوري لولي الأمر بالحضور والغياب والنتائج والمستحقات
                </p>
                <div className="space-y-3 mb-5">
                  {["حضور في ثانية بالكارت", "رسائل واتساب لولي الأمر", "امتحانات ومدفوعات وتقارير", "بدون اشتراك شهري"].map((feature, i) => (
                    <motion.div
                      key={feature}
                      className="flex items-center gap-2 text-sm text-zinc-400"
                      initial={{ opacity: 0, x: 20 }}
                      whileInView={{ opacity: 1, x: 0 }}
                      viewport={{ once: true }}
                      transition={{ delay: 0.3 + i * 0.1 }}
                    >
                      <div className="w-1.5 h-1.5 rounded-full bg-sky-400" />
                      {feature}
                    </motion.div>
                  ))}
                </div>
                <Link
                  href="/centers"
                  className="mt-auto flex items-center justify-center gap-2 py-3 px-4 rounded-xl bg-sky-600 hover:bg-sky-500 text-white font-medium transition-colors w-full"
                >
                  <span>تفاصيل سيستم السناتر</span>
                  <ArrowLeft className="w-4 h-4" />
                </Link>
              </CardContent>
            </Card>
          </motion.div>

          {/* Card - تطبيق عيادات */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay: 0.15 }}
            className="md:col-span-5"
          >
            <Card className="group overflow-hidden border-zinc-800/50 bg-gradient-to-br from-zinc-900/50 to-teal-900/20 hover:border-teal-700/50 transition-all duration-300 rounded-2xl">
              <CardContent className="p-6 md:p-8">
                <div className="flex flex-col md:flex-row md:items-center gap-6">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-3">
                      <motion.div
                        className="w-10 h-10 rounded-xl bg-teal-900/30 flex items-center justify-center"
                        whileHover={{ rotate: [0, -10, 10, 0] }}
                        transition={{ duration: 0.5 }}
                      >
                        <Stethoscope className="w-5 h-5 text-teal-400 group-hover:text-teal-300 transition-colors" />
                      </motion.div>
                      <p className="font-heading font-semibold text-zinc-100">تطبيق عيادات للأطباء</p>
                    </div>
                    <p className="text-zinc-500 text-sm mb-4 max-w-xl">
                      تطبيق متكامل باسم عيادتك — نسخ للمريض والدكتور والمساعد، حجوزات، استشارات أونلاين، وصفات إلكترونية وAI
                    </p>
                    <div className="flex flex-wrap gap-2">
                      {["حجوزات أونلاين", "وصفات إلكترونية", "AI للسعرات", "Android + iOS"].map((feature) => (
                        <span
                          key={feature}
                          className="px-3 py-1 rounded-full text-xs bg-teal-900/30 border border-teal-800/40 text-teal-300"
                        >
                          {feature}
                        </span>
                      ))}
                    </div>
                  </div>
                  <Link
                    href="/clinic"
                    className="inline-flex items-center justify-center gap-2 py-3 px-6 rounded-xl bg-teal-600 hover:bg-teal-500 text-white font-medium transition-colors shrink-0"
                  >
                    <span>تفاصيل تطبيق العيادات</span>
                    <ArrowLeft className="w-4 h-4" />
                  </Link>
                </div>
              </CardContent>
            </Card>
          </motion.div>

          {/* Card 2 - متاجر إلكترونية (narrower - 2 cols) */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay: 0.2 }}
            className="md:col-span-2"
          >
            <Card className="group h-full overflow-hidden border-zinc-800/50 bg-zinc-900/50 hover:border-zinc-700/50 transition-all duration-300 rounded-2xl">
              <CardContent className="p-6 flex flex-col h-full">
                <div className="flex items-center gap-3 mb-3">
                  <motion.div
                    className="w-10 h-10 rounded-xl bg-emerald-900/30 flex items-center justify-center"
                    animate={{ scale: [1, 1.1, 1] }}
                    transition={{ duration: 2, repeat: Number.POSITIVE_INFINITY, ease: "easeInOut" }}
                  >
                    <ShoppingCart className="w-5 h-5 text-emerald-400 group-hover:text-emerald-300 transition-colors" />
                  </motion.div>
                  <p className="font-heading font-semibold text-zinc-100">متاجر إلكترونية</p>
                </div>
                <p className="text-zinc-500 text-sm mb-5">متاجر احترافية مع بوابات دفع متعددة وإدارة مخزون متقدمة</p>
                <div className="mt-auto space-y-3">
                  {["بوابات دفع متعددة", "إدارة المخزون", "تقارير المبيعات", "تطبيق موبايل"].map((feature, i) => (
                    <motion.div
                      key={feature}
                      className="flex items-center gap-2 text-sm text-zinc-400"
                      initial={{ opacity: 0, x: 20 }}
                      whileInView={{ opacity: 1, x: 0 }}
                      viewport={{ once: true }}
                      transition={{ delay: 0.3 + i * 0.1 }}
                    >
                      <div className="w-1.5 h-1.5 rounded-full bg-emerald-400" />
                      {feature}
                    </motion.div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </motion.div>

          {/* Card 4 - نظام إدارة المطاعم (wider - 3 cols) */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay: 0.4 }}
            className="md:col-span-3"
            id="dine"
          >
            <Card className="group h-full overflow-hidden border-zinc-800/50 bg-gradient-to-br from-zinc-900/50 to-amber-900/20 hover:border-amber-700/50 transition-all duration-300 rounded-2xl">
              <CardContent className="p-6 flex flex-col h-full">
                <div className="flex items-center gap-3 mb-3">
                  <motion.div
                    className="w-10 h-10 rounded-xl bg-amber-900/40 flex items-center justify-center"
                    animate={{ rotate: [0, 5, -5, 0] }}
                    transition={{ duration: 2, repeat: Number.POSITIVE_INFINITY }}
                  >
                    <UtensilsCrossed className="w-5 h-5 text-amber-400 group-hover:text-amber-300 transition-colors" />
                  </motion.div>
                  <div>
                    <p className="font-heading font-semibold text-zinc-100">نظام إدارة المطاعم والكافيهات</p>
                    <span className="text-xs text-amber-400">Anmka Dine</span>
                  </div>
                </div>
                <p className="text-zinc-500 text-sm mb-5">
                  منيو QR لكل ترابيزة، والطلب يوصل المطبخ والكاشير والويتر لحظيًا — وانت تتابع كل جنيه من لوحة التحكم
                </p>
                <div className="grid grid-cols-2 gap-3 mb-4">
                  {[
                    { icon: QrCode, label: "منيو QR", desc: "من غير تطبيق" },
                    { icon: BellRing, label: "نداء الويتر", desc: "إشعارات فورية" },
                    { icon: MonitorSmartphone, label: "كاشير ومطبخ", desc: "شاشات لحظية" },
                    { icon: BarChart3, label: "تقارير", desc: "مبيعات وذروة" },
                  ].map((feature, i) => (
                    <motion.div
                      key={feature.label}
                      className="flex items-center gap-3 p-3 rounded-lg bg-zinc-800/50 border border-zinc-700/30"
                      initial={{ opacity: 0, scale: 0.9 }}
                      whileInView={{ opacity: 1, scale: 1 }}
                      viewport={{ once: true }}
                      transition={{ delay: 0.3 + i * 0.1 }}
                    >
                      <feature.icon className="w-5 h-5 text-amber-400 shrink-0" />
                      <div>
                        <p className="text-sm text-zinc-200">{feature.label}</p>
                        <p className="text-xs text-zinc-500">{feature.desc}</p>
                      </div>
                    </motion.div>
                  ))}
                </div>
                <Link
                  href="/dine"
                  className="mt-auto flex items-center justify-center gap-2 py-3 px-4 rounded-xl bg-amber-600 hover:bg-amber-500 text-white font-medium transition-colors"
                >
                  <span>تفاصيل نظام المطاعم</span>
                  <ArrowLeft className="w-4 h-4" />
                </Link>
              </CardContent>
            </Card>
          </motion.div>

          {/* Card 3 - تطبيقات موبايل (narrower - 2 cols) */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay: 0.3 }}
            className="md:col-span-2"
          >
            <Card className="group h-full overflow-hidden border-zinc-800/50 bg-zinc-900/50 hover:border-zinc-700/50 transition-all duration-300 rounded-2xl">
              <CardContent className="p-6 flex flex-col h-full">
                <div className="flex items-center gap-3 mb-3">
                  <motion.div
                    className="w-10 h-10 rounded-xl bg-purple-900/30 flex items-center justify-center"
                    whileHover={{ y: -2 }}
                  >
                    <Smartphone className="w-5 h-5 text-purple-400 group-hover:text-purple-300 transition-colors" />
                  </motion.div>
                  <p className="font-heading font-semibold text-zinc-100">تطبيقات Flutter</p>
                </div>
                <p className="text-zinc-500 text-sm mb-5">تطبيقات موبايل بتقنية Flutter لـ iOS و Android</p>
                <div className="flex justify-center gap-4 mt-auto">
                  {[
                    { icon: "/ios-icon.png", label: "iOS" },
                    { icon: "/android-icon.png", label: "Android" },
                  ].map((platform, i) => (
                    <motion.div
                      key={platform.label}
                      className="flex flex-col items-center justify-center w-20 h-20 rounded-xl bg-zinc-800/80 border border-zinc-700/50"
                      initial={{ y: 0 }}
                      animate={{ y: [0, -4, 0] }}
                      transition={{
                        duration: 1.5,
                        delay: i * 0.15,
                        repeat: Number.POSITIVE_INFINITY,
                        repeatDelay: 2,
                      }}
                      whileHover={{ scale: 1.1, y: -4 }}
                    >
                      <img src={platform.icon} alt={platform.label} className="w-10 h-10 mb-1 object-contain" />
                      <span className="text-zinc-400 text-xs">{platform.label}</span>
                    </motion.div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </motion.div>

          {/* Card 5 - التقنيات (full width - 5 cols) */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay: 0.5 }}
            className="md:col-span-3"
          >
            <Card className="group overflow-hidden border-zinc-800/50 bg-zinc-900/50 hover:border-zinc-700/50 transition-all duration-300 rounded-2xl">
              <CardContent className="p-6">
                <div className="flex items-center gap-3 mb-4">
                  <motion.div
                    className="w-10 h-10 rounded-xl bg-zinc-800 flex items-center justify-center"
                    whileHover={{ rotate: 180 }}
                    transition={{ duration: 0.4 }}
                  >
                    <Code2 className="w-5 h-5 text-zinc-400 group-hover:text-zinc-200 transition-colors" />
                  </motion.div>
                  <p className="font-heading font-semibold text-zinc-100">التقنيات التي نستخدمها</p>
                </div>
                <div className="grid grid-cols-4 gap-3">
                  {technologies.map((tech, i) => (
                    <motion.div
                      key={tech.name}
                      initial={{ opacity: 0, y: 10 }}
                      whileInView={{ opacity: 1, y: 0 }}
                      viewport={{ once: true }}
                      transition={{ duration: 0.3, delay: 0.3 + i * 0.05 }}
                      whileHover={{ scale: 1.1, y: -2 }}
                      className="aspect-square rounded-xl border border-zinc-800 bg-zinc-800/50 flex items-center justify-center cursor-pointer hover:border-zinc-600 transition-colors"
                    >
                      <span className="text-xs text-zinc-400 font-medium">{tech.name}</span>
                    </motion.div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </motion.div>
        </div>
      </div>
    </section>
  )
}
